import Cookies from 'universal-cookie';
import axios from 'axios';
import uuid from 'react-uuid';
import { useAppConfig } from '../AppConfig';

const cookies = new Cookies();

// Returns the app configuration (null until it is loaded).
// Reads the context directly: copying it into local state cost every caller an
// extra render. The decimalSeparator cookie is written once by AppConfigProvider.
export const useConfig = () => useAppConfig()?.config ?? null;

export const API_GET = {
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
    Authorization: 'Bearer ' + cookies.get('accessToken'),
  },
  method: 'GET',
  mode: 'cors',
  //credentials: 'include',
};

export const API_GET_NO_TOKEN = {
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
  method: 'GET',
  mode: 'cors',
  //credentials: 'include',
};

export function API_DELETE() {
  // in URL ".../id"
  return {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: 'Bearer ' + cookies.get('accessToken'),
      'X-Transaction-Id': uuid(),
    },
    method: 'DELETE',
    mode: 'cors',
    //credentials: 'include',
  };
}

export function API_POST(data = '') {
  return {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: 'Bearer ' + cookies.get('accessToken'),
      'X-Transaction-Id': uuid(),
    },
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify(data),
    //credentials: 'include',
  };
}

export function API_POST_NO_TOKEN(data = '') {
  return {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify(data),
    //credentials: 'include',
  };
}

export function API_PATCH(data = '') {
  data._method = 'PATCH';
  return {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: 'Bearer ' + cookies.get('accessToken'),
      'X-Transaction-Id': uuid(),
    },
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify(data),
    //credentials: 'include',
  };
}

export function API_PUT(data = '') {
  data._method = 'PUT';
  return {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: 'Bearer ' + cookies.get('accessToken'),
      'X-Transaction-Id': uuid(),
    },
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify(data),
    //credentials: 'include',
  };
}

export function checkAuthentication(status) {
  // Only reload when a (stale) session token actually exists. Without a
  // token a reload can never fix a 401 and causes infinite refresh loops
  // on public pages (e.g. customer-data-form).
  if (status === 401 && cookies.get('accessToken')) window.location.reload();
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

// Error bodies are not always JSON (e.g. an HTML page from a proxy on 502/504).
async function readErrorBody(res) {
  let text = '';
  try {
    text = await res.text();
  } catch {
    // body could not be read
  }
  try {
    return JSON.parse(text);
  } catch {
    return { message: text || res.statusText || `Request failed (${res.status})` };
  }
}

// Callback errors must never trigger a retry or escape as unhandled rejections.
function safeCall(callback, ...args) {
  try {
    callback(...args);
  } catch (callbackError) {
    console.error(callbackError);
  }
}

export async function DBRequest({
  config,
  path = '',
  method,
  onResponse = () => {},
  onError = () => {},
  onLoading = () => {},
  onFeedback = () => {},
  noAuth,
  customPath,
  debug,
  disableRetry,
  retryMax = 3,
  retryArr = [500, 502, 503, 504],
  retryDelayMs = 500,
}) {
  if (!customPath && !config) return;

  const requestOptions = method || API_GET;
  const hasAuthHeader = Boolean(requestOptions?.headers?.Authorization);
  const URL = customPath ? path : config.API_URL + path;

  // Report the FIRST error, not the last one.
  let firstError = null;
  let firstErrorStatus = null;

  onLoading(true);

  // Every attempt counts, so a failing request ends after `retryMax` tries.
  for (let attempt = 1; attempt <= retryMax; attempt++) {
    let res;
    try {
      res = await fetch(URL, requestOptions);
    } catch (networkError) {
      // fetch rejects on offline, DNS, CORS or a dropped connection
      if (firstError === null) {
        firstError = { message: networkError?.message || 'Network error' };
        firstErrorStatus = 0;
      }
      if (disableRetry || isOffline() || attempt >= retryMax) break;
      await wait(retryDelayMs * 2 ** (attempt - 1));
      continue;
    }

    if (res.ok) {
      let response;
      try {
        response = await res.json();
      } catch {
        // e.g. 204 No Content
        safeCall(onLoading, false);
        return;
      }
      debug && console.log(response);
      safeCall(onResponse, response, true);
      safeCall(onLoading, false);
      safeCall(onFeedback, response?.message, 'success');
      return;
    }

    const error = await readErrorBody(res);
    if (firstError === null) {
      firstError = error;
      firstErrorStatus = res.status;
    }

    if (hasAuthHeader && !noAuth) checkAuthentication(res.status);

    const isRetryable = !disableRetry && retryArr.includes(res.status);
    if (!isRetryable || attempt >= retryMax) break;
    await wait(retryDelayMs * 2 ** (attempt - 1));
  }

  safeCall(onFeedback, firstError?.message, 'error', firstError);
  safeCall(onError, firstError, firstErrorStatus);
  safeCall(onLoading, false);
}

export function DBGET({
  url,
  path = '',
  onResponse = () => {},
  onLoading = () => {},
  onFeedback = () => {},
  debug,
}) {
  axios
    .get(url + path)
    .then((response) => {
      onResponse(response.data, true);
      onLoading(false);
      onFeedback(response.data.message, 'success');
      debug && console.log(response.data);
    })
    .catch((error) => {
      onLoading(false);
      onResponse(error, false);
      const errorMessage = error.response
        ? error.response.data.message
        : error.message;
      const errorStatus = error.response ? error.response.status : null;
      onFeedback(errorMessage, 'error', error);
      checkAuthentication(errorStatus);
      console.error(errorMessage, error);
    });
}

export function DBPOST({
  url,
  path = '',
  data,
  onResponse = () => {},
  onError = () => {},
  onLoading = () => {},
  onFeedback = () => {},
  debug,
}) {
  axios
    .post(url + path, data)
    .then((response) => {
      onResponse(response.data, true);
      onLoading(false);
      onFeedback(response.data.message, 'success');
      debug && console.log(response.data);
    })
    .catch((error) => {
      onLoading(false);
      onResponse(error, false);
      const errorMessage = error.response
        ? error.response.data.message
        : error.message;
      const errorStatus = error.response ? error.response.status : null;
      onFeedback(errorMessage, 'error', error);
      checkAuthentication(errorStatus);
      console.error(errorMessage, error);
    });
}

export function DBRequestModules({
  config,
  path,
  method,
  onResponse = () => {},
  onLoading = () => {},
  onFeedback = () => {},
  debug,
}) {
  if (!config) return;
  onLoading(true);
  fetch(config.API_MODULES + path, method || API_GET)
    .then((res) => {
      if (!res.ok) {
        return res.json().then((error) => {
          onFeedback(error.message, 'error', error);
          checkAuthentication(res.status);
          throw Error(res.status + ' | ' + error.message);
        });
      } else return res.json();
    })
    .then((response) => {
      debug && console.log(response);
      onResponse(response);
      onLoading(false);
      onFeedback('Success', 'success');
    })
    .catch((error) => {
      console.log(error.message, error.trace);
    });
}

export function DBUploadData({
  config = {},
  path,
  formData,
  onProgressChange = () => {},
  onFinished = () => {},
  onResponse = () => {},
  onFeedback = () => {},
  debug = false,
  onError = () => {},
}) {
  onFinished(false);
  const headers = {
    Authorization: 'Bearer ' + cookies.get('accessToken'),
    'Content-Type': 'multipart/form-data',
  };

  axios
    .post(config.API_URL + path, formData, {
      headers: headers,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgressChange(percentCompleted);
        // Update progress bar or UI with the `percentCompleted` value
      },
    })
    .then((response) => {
      // Handle the server's response
      onFeedback(response);
      onFinished(true);
      onResponse(response);
      debug && console.log('DEBUG', response);
    })
    .catch((error) => {
      // Handle errors
      onFeedback(error);
      onError(error);
      debug && console.log('DEBUG', error);
    });
}

export function DBDownloadData(
  apiConfig,
  url,
  setPercent,
  setFinished,
  handleFeedback,
  debug = false,
) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', apiConfig.API_URL + url, true);
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.setRequestHeader('accept', 'application/json');
  xhr.setRequestHeader('Authorization', 'Bearer ' + cookies.get('accessToken'));

  xhr.addEventListener('progress', (event) => {
    const percent = (event.loaded / event.total) * 100;
    setPercent(percent);
  });

  xhr.addEventListener('load', () => {
    const res = JSON.parse(xhr.responseText);
    handleFeedback(res.message, 'success');
    setFinished(true);
    debug && console.log(res.data);
  });

  xhr.addEventListener('error', (error) => {
    handleFeedback(error.message, 'error');
    setFinished(true);
    console.log(error.message);
  });

  xhr.send();
}

export async function SearchRequest({
  baseUrl,
  path,
  data = {},
  method = 'get',
  onResponse = () => {},
  onError = () => {},
  onLoading = () => {},
}) {
  onLoading(true);
  const token = cookies.get('searchToken');

  await axios({
    method: method,
    url: `${baseUrl}/${path}`,
    data: data,
    headers: {
      //...axios.defaults.headers.common, // keep defaults
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then((response) => {
      onResponse(response.data);
      onLoading(false);
    })
    .catch((error) => {
      console.error('Error fetching data:', error);
      onError(error);
      onLoading(false);
    });
}
